import {UserDefinedObjectTemplateAdditionalColumnSourceType as UdoColumnSource} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import type {UserDefinedObjectTemplateAdditionalColumn as UdoColumn} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {getUserDefinedObjectAdditionalColumnDetails} from "~/domainModels/userDefinedObjectAdditionalColumns";
import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import {useCapabilityUdoColumns} from "~/contexts/workspaceContext";
import {token} from "@atlaskit/tokens";
import {DatePickerInput, DateTimePicker} from "@mantine/dates";
import type {ReactElement} from "react";
import {useMemo} from "react";
import {LinkedJourneyEventsOverviewSection} from "~/components/eventComponents/linkedJourneyEventsOverviewSection";
import {AnankeTempFormCollapsibleSection} from "~/components/anankeTemp/anankeTempFormCollapsibleSection";
import {AnankeTempVerticalSpacer} from "~/components/anankeTemp/anankeTempVerticalSpacer";
import {getProductCatalogueTemplateIdFromAdditionalColumnConfiguration} from "~/domainModels/additionalColumns";

import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {OttopilotEventViewPanelSectionKind} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTableViewColumn";

import {getSystemAdditionalColumnDetails} from "~/domainModels/systemAdditionalColumns";
import type {ProductCatalogue} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/productCataloguesWorkspaceCapability/domain/ProductCatalogue";
import {
    EMOTORAD__CUSTOMERS__WALKINS_COUNT_COLUMN_ID,
    EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID,
    EMOTORAD__EVENT__SALES__MODEL__COLUMN_ID,
    EMOTORAD__EVENT__SALES__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__PREFERRED_COLOR__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_TIME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__LAST_WALKIN_DATE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__TEST_RIDE_TAKEN__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__WALKINS_COUNT__COLUMN_ID,
    EMOTORAD__PRODUCT_CATALOGUE__COLOR_TEMPLATE_ID,
    EMOTORAD__PRODUCT_CATALOGUE__VARIANT_TEMPLATE_ID,
    emotoradEventContainerDispositions,
    formatEmotoradScalarForUnknownColumnDisplay,
    EMOTORAD__EVENT__ENQUIRY__CREATED_BY_NAME__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__GENDER__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CREATED_BY_NAME__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CREATED_BY_ROLE__COLUMN_ID,
    EMOTORAD__USER__GENDER__COLUMN_ID,
    EmotoradEventTypes,
    emotoradGenders,
} from "~/domainModels/workspaceSpecific/emotorad";
import {DULUX_EVENT_TEMPLATE_EVENT_OWNER_COLUMN_IDS, formatDuluxEventUserReferenceDisplay, isDuluxEventTemplateId, isDuluxWorkspace} from "~/domainModels/workspaceSpecific/dulux";
import {AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID} from "~/domainModels/workspaceSpecific/amaron";
import {DULUX__EVENT__CALLS__SCHEDULE_DATE__COLUMN_ID} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/dulux/Events";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import {ItemBuilder} from "@vesta/global-common-typescript/components/itemBuilder";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventWithEventContainer} from "~/utilities/apis/api";
import {
    iso8601DateTimeUtcToTriggerSchedulePickerValue,
    triggerSchedulePickerValueToIso8601DateTimeUtc,
} from "~/utilities/eventContainerTriggerEvent";
import {formatOttopilotDisplayDateTime, getUserDisplayName} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import {useBtlActivities} from "~/contexts/workspaceBtlActivitiesContext";
import type {BtlActivity} from "~/domainModels/btlActivities";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {useOrganizationalUnitTemplates, useProductCatalogueTemplates, useWorkspace, useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";
import {useProductCatalogue} from "~/contexts/productCatalogueContext";
import {getProductCatalogueDisplayName} from "~/utilities/productCatalogueDisplayName";
import {AnankeSelect} from "@vesta/ananke/components/anankeSelect";
import {AnankeTextField} from "@vesta/ananke/components/anankeTextField";
import {resolveColumnValue, type ColumnValueDeps} from "~/components/eventComponents/eventViewPanel";
import {
    combineEmotoradTestRidePickerDateAndSlotHhMmToUtcIso,
    formatEmotoradTestRideDateOnlyForDisplay,
    formatEmotoradTestRideShortRangeLabelFromSlotHhMm,
    formatEmotoradTestRideTimeSlotDisplay,
    getEmotoradTestRideEffectiveSlotHhMmFromEventRow,
    getEmotoradTestRideHourlyTimeSlotOptions,
    normalizeEmotoradTestRideSlotHhMmString,
    parseEmotoradTestRideDatePickerDayToLocalDate,
    splitEmotoradTestRideStoredInstantToPickerDateAndSlot,
} from "~/workspaceConfig/hostedForms/shared/emotorad/emotoradTestrideTimeSlots";
import {
    getEventContainerOrganizationalUnitId,
    getEventDispositionId,
    requireEventTemplate,
} from "~/utilities/ottopilotSemanticColumns";

function coalesceEmotoradTestrideCustomerGeoFromEvent(eventWithDiff: EventWithEventContainer, journeyColumnId: Uuid, eventColumnId: Uuid): unknown {
    const fromJourney = eventWithDiff.eventContainer.additionalColumnValues[journeyColumnId];
    if (fromJourney !== null && fromJourney !== undefined && String(fromJourney).trim() !== "") {
        return fromJourney;
    }
    return eventWithDiff.additionalColumnValues[eventColumnId];
}

function resolveEmotoradTestrideJourneyGeoValue(eventWithDiff: EventWithEventContainer, journeyColumnId: Uuid): unknown {
    if (String(journeyColumnId) === String(EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID)) {
        return coalesceEmotoradTestrideCustomerGeoFromEvent(eventWithDiff, journeyColumnId, EMOTORAD__EVENT__TESTRIDES__CUSTOMER_PINCODE__COLUMN_ID);
    }
    if (String(journeyColumnId) === String(EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID)) {
        return coalesceEmotoradTestrideCustomerGeoFromEvent(eventWithDiff, journeyColumnId, EMOTORAD__EVENT__TESTRIDES__CUSTOMER_CITY__COLUMN_ID);
    }
    if (String(journeyColumnId) === String(EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID)) {
        return coalesceEmotoradTestrideCustomerGeoFromEvent(eventWithDiff, journeyColumnId, EMOTORAD__EVENT__TESTRIDES__CUSTOMER_STATE__COLUMN_ID);
    }
    return eventWithDiff.eventContainer.additionalColumnValues[journeyColumnId];
}

const EMOTORAD_CREATED_BY_RESOLVE_VIA_EVENT_AUTHOR_COLUMN_IDS = new Set<string>([
    String(EMOTORAD__EVENT__SALES__CREATED_BY_NAME__COLUMN_ID),
    String(EMOTORAD__EVENT__SALES__CREATED_BY_ROLE__COLUMN_ID),
    String(EMOTORAD__EVENT__ENQUIRY__CREATED_BY_NAME__COLUMN_ID),
    String(EMOTORAD__EVENT__ENQUIRY__CREATED_BY_ROLE__COLUMN_ID),
]);

function resolveEmotoradCreatedByColumnDisplayIfApplicable(getAdditionalColumns: CapabilityUdoColumnResolver,
    columnConfig: {id: Uuid; label: string; columnId: Uuid | string; columnType: "OttopilotEventContainer" | "OttopilotEvent"; isAdditionalColumn: boolean; eventTemplateId?: Uuid},
    deps: ColumnValueDeps,
): string | null {
    if (
        columnConfig.columnType !== "OttopilotEvent" ||
        columnConfig.isAdditionalColumn !== true ||
        EMOTORAD_CREATED_BY_RESOLVE_VIA_EVENT_AUTHOR_COLUMN_IDS.has(String(columnConfig.columnId)) !== true
    ) {
        return null;
    }
    return resolveColumnValue(
        getAdditionalColumns,
        {
            id: columnConfig.id,
            label: columnConfig.label,
            columnId: columnConfig.columnId,
            isAdditionalColumn: true,
            columnType: "OttopilotEvent",
            eventTemplateId: columnConfig.eventTemplateId,
        },
        deps,
    );
}

// Type for a column in the journeyDetailsPage.columnGrouping
export type EventColumnConfig = {
    id: Uuid;
    columnId: Uuid | string;
    columnType: "OttopilotEventContainer" | "OttopilotEvent";
    eventTemplateId?: Uuid;
    isAdditionalColumn: boolean;
    isEditable?: boolean;
    /** Mandatory (e.g. Emotorad M); show * and validate when editable. */
    isRequired?: boolean;
    label: string;
};

/** Column sections used by view/edit grouping UIs (excludes overview-only sections). */
export type EventColumnGroupWithColumns = {
    kind?: OttopilotEventViewPanelSectionKind.columnGroup;
    id: Uuid;
    displayName: string;
    columns: Array<EventColumnConfig>;
};

export type EventColumnGroupOverview = {
    kind: OttopilotEventViewPanelSectionKind.eventOverview;
    id: Uuid;
    displayName?: string;
    initialIsOpen?: boolean;
    className?: string;
};

// Type for a column group in the journeyDetailsPage.columnGrouping (may include overview).
export type EventColumnGroup = EventColumnGroupWithColumns | EventColumnGroupOverview;

/** Emotorad sale create filters variants by selected model; mirror that for the sales edit panel select. */
function filterEmotoradSalesModelVariantSelectOptions(params: {
    fullOptions: Array<{id: Uuid; displayName: string}>;
    products: ReadonlyArray<ProductCatalogue>;
    selectedModelId: unknown;
    currentVariantId: unknown;
}): Array<{id: Uuid; displayName: string}> {
    const {fullOptions, products, selectedModelId, currentVariantId} = params;
    const modelStr = selectedModelId !== null && selectedModelId !== undefined && String(selectedModelId).trim() !== "" ? String(selectedModelId) : null;
    if (modelStr === null) {
        return fullOptions;
    }
    const allowedIds = new Set(
        products
            .filter((p) => p.templateId === EMOTORAD__PRODUCT_CATALOGUE__VARIANT_TEMPLATE_ID && p.parentId !== null && String(p.parentId) === modelStr)
            .map((p) => p.id),
    );
    const filtered = fullOptions.filter((o) => allowedIds.has(o.id));
    const variantStr = currentVariantId !== null && currentVariantId !== undefined && String(currentVariantId).trim() !== "" ? String(currentVariantId) : null;
    if (variantStr === null) {
        return filtered.length > 0 ? filtered : fullOptions;
    }
    if (filtered.some((o) => String(o.id) === variantStr) === true) {
        return filtered;
    }
    const orphan = fullOptions.find((o) => String(o.id) === variantStr);
    if (orphan !== undefined) {
        return [orphan, ...filtered];
    }
    return filtered.length > 0 ? filtered : fullOptions;
}

/** Colours under the selected variant, or under any variant of the selected model (same as sale create). */
function filterEmotoradSalesPreferredColorSelectOptions(params: {
    fullOptions: Array<{id: Uuid; displayName: string}>;
    products: ReadonlyArray<ProductCatalogue>;
    selectedModelId: unknown;
    selectedVariantId: unknown;
    currentColorId: unknown;
}): Array<{id: Uuid; displayName: string}> {
    const {fullOptions, products, selectedModelId, selectedVariantId, currentColorId} = params;
    const modelStr = selectedModelId !== null && selectedModelId !== undefined && String(selectedModelId).trim() !== "" ? String(selectedModelId) : null;
    const variantStr =
        selectedVariantId !== null && selectedVariantId !== undefined && String(selectedVariantId).trim() !== "" ? String(selectedVariantId) : null;

    if (modelStr === null && variantStr === null) {
        return fullOptions;
    }

    const variantIdSet = new Set<Uuid>();
    if (modelStr !== null) {
        for (const p of products) {
            if (p.templateId === EMOTORAD__PRODUCT_CATALOGUE__VARIANT_TEMPLATE_ID && p.parentId !== null && String(p.parentId) === modelStr) {
                variantIdSet.add(p.id);
            }
        }
    }

    const allowedColorIds = new Set<Uuid>();
    for (const p of products) {
        if (p.templateId !== EMOTORAD__PRODUCT_CATALOGUE__COLOR_TEMPLATE_ID) {
            continue;
        }
        if (variantStr !== null) {
            if (p.parentId !== null && String(p.parentId) === variantStr) {
                allowedColorIds.add(p.id);
            }
        } else if (modelStr !== null) {
            if (p.parentId !== null && variantIdSet.has(p.parentId) === true) {
                allowedColorIds.add(p.id);
            }
        }
    }

    const filtered = fullOptions.filter((o) => allowedColorIds.has(o.id));
    const colorVal = currentColorId !== null && currentColorId !== undefined && String(currentColorId).trim() !== "" ? String(currentColorId) : null;
    if (colorVal === null) {
        return filtered.length > 0 ? filtered : fullOptions;
    }
    if (filtered.some((o) => String(o.id) === colorVal) === true) {
        return filtered;
    }
    const orphan = fullOptions.find((o) => String(o.id) === colorVal);
    if (orphan !== undefined) {
        return [orphan, ...filtered];
    }
    return filtered.length > 0 ? filtered : fullOptions;
}

function emotoradNonEmptyIdString(raw: unknown): string | null {
    if (raw === null || raw === undefined || String(raw).trim() === "") {
        return null;
    }
    return String(raw);
}

/** IDs allowed in {@link ProductPicker} for sales model variant — variants under selected model (+ current value). */
function getEmotoradSalesModelVariantAllowedProductIds(
    products: ReadonlyArray<ProductCatalogue>,
    selectedModelId: unknown,
    currentVariantId: unknown,
): ReadonlySet<Uuid> | undefined {
    const modelStr = emotoradNonEmptyIdString(selectedModelId);
    if (modelStr === null) {
        return undefined;
    }
    const allowed = new Set<Uuid>();
    for (const p of products) {
        if (p.templateId === EMOTORAD__PRODUCT_CATALOGUE__VARIANT_TEMPLATE_ID && p.parentId !== null && String(p.parentId) === modelStr) {
            allowed.add(p.id);
        }
    }
    const cur = emotoradNonEmptyIdString(currentVariantId);
    if (cur !== null) {
        const curUuid = cur as Uuid;
        if (allowed.has(curUuid) === false && products.some((p) => p.id === curUuid) === true) {
            allowed.add(curUuid);
        }
    }
    return allowed;
}

/** IDs allowed for sales preferred colour — colours under variant, or under any variant of model (+ current value). */
function getEmotoradSalesPreferredColorAllowedProductIds(
    products: ReadonlyArray<ProductCatalogue>,
    selectedModelId: unknown,
    selectedVariantId: unknown,
    currentColorId: unknown,
): ReadonlySet<Uuid> | undefined {
    const modelStr = emotoradNonEmptyIdString(selectedModelId);
    const variantStr = emotoradNonEmptyIdString(selectedVariantId);
    if (modelStr === null && variantStr === null) {
        return undefined;
    }
    const variantIdSet = new Set<Uuid>();
    if (modelStr !== null) {
        for (const p of products) {
            if (p.templateId === EMOTORAD__PRODUCT_CATALOGUE__VARIANT_TEMPLATE_ID && p.parentId !== null && String(p.parentId) === modelStr) {
                variantIdSet.add(p.id);
            }
        }
    }
    const allowed = new Set<Uuid>();
    for (const p of products) {
        if (p.templateId !== EMOTORAD__PRODUCT_CATALOGUE__COLOR_TEMPLATE_ID) {
            continue;
        }
        if (variantStr !== null) {
            if (p.parentId !== null && String(p.parentId) === variantStr) {
                allowed.add(p.id);
            }
        } else if (modelStr !== null) {
            if (p.parentId !== null && variantIdSet.has(p.parentId) === true) {
                allowed.add(p.id);
            }
        }
    }
    const cur = emotoradNonEmptyIdString(currentColorId);
    if (cur !== null) {
        const curUuid = cur as Uuid;
        if (allowed.has(curUuid) === false && products.some((p) => p.id === curUuid) === true) {
            allowed.add(curUuid);
        }
    }
    return allowed;
}

function isEventOverviewSection(group: EventColumnGroup): group is EventColumnGroupOverview {
    return group.kind === OttopilotEventViewPanelSectionKind.eventOverview;
}

function isColumnGroupSection(group: EventColumnGroup): group is EventColumnGroupWithColumns {
    return group.kind !== OttopilotEventViewPanelSectionKind.eventOverview;
}

/** Emotorad "Assigned Dealer" may store OU id while the journey column type is a plain string (edit/view panels). */
function tryFormatEmotoradAssignedDealerJourneyValue(
    columnConfig: EventColumnConfig,
    value: unknown,
    organizationalUnits: ReturnType<typeof useOrganizationalUnits>,
    organizationalUnitTemplates: ReturnType<typeof useOrganizationalUnitTemplates>,
): string | undefined {
    if (
        columnConfig.columnType !== "OttopilotEventContainer" ||
        columnConfig.isAdditionalColumn !== true ||
        String(columnConfig.columnId) !== String(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID) ||
        typeof value !== "string"
    ) {
        return undefined;
    }
    const trimmed = value.trim();
    if (trimmed === "") {
        return undefined;
    }
    const ou = organizationalUnits.find((u) => u.id === trimmed);
    if (ou === undefined) {
        return undefined;
    }
    const ouTemplate = organizationalUnitTemplates.find((t) => t.id === ou.templateId);
    if (ouTemplate === undefined) {
        return undefined;
    }
    return processOttopilotStringTemplate(ouTemplate.instanceDisplayNameTemplate, ou.additionalColumnValues);
}

function getDispositionLevelsFromEvent(
    event: EventWithEventContainer,
    eventTemplates: ReadonlyArray<OttopilotEventTemplate>,
): {l1: string; l2: string; l3: string} {
    const dispositionId = getEventDispositionId(
        event,
        requireEventTemplate(eventTemplates, event.templateId),
    );
    const disposition = Object.values(emotoradEventContainerDispositions).find((item) => item.id === dispositionId);
    if (disposition === undefined) {
        return {l1: "", l2: "", l3: ""};
    }
    const parts = disposition.flatDisposition.split("_");
    return {
        l1: parts[0] ?? "",
        l2: parts[1] ?? "",
        l3: parts.length <= 2 ? "" : parts.slice(2).join("_"),
    };
}

function isEmotoradTestRideTakenLabel(normalizedLabel: string): boolean {
    return normalizedLabel === "test ride taken ?" || normalizedLabel === "test ride taken?" || normalizedLabel === "test ride taken";
}

function coalesceEmotoradWalkinTestRideTakenValue(event: EventWithEventContainer, columnConfig: EventColumnConfig, normalizedLabel: string): unknown {
    if (columnConfig.columnType !== "OttopilotEvent" || columnConfig.isAdditionalColumn !== true || isEmotoradTestRideTakenLabel(normalizedLabel) !== true) {
        return event.additionalColumnValues[columnConfig.columnId as Uuid];
    }
    const cid = columnConfig.columnId as Uuid;
    const candidates = [
        event.additionalColumnValues[cid],
        event.additionalColumnValues[EMOTORAD__EVENT__WALKINS__TEST_RIDE_TAKEN__COLUMN_ID],
        event.additionalColumnValues[EMOTORAD__EVENT__CALLS__TEST_RIDE_TAKEN__COLUMN_ID],
    ];
    return candidates.find((x) => x !== null && x !== undefined && x !== "") ?? null;
}

type EventDetailsColumnsProps = {
    columnGrouping: Array<EventColumnGroup>;
    journeysModuleConfiguration: EventContainerTemplate;
    eventTemplates: Array<OttopilotEventTemplate>;
    eventWithDiff: EventWithEventContainer;
    event: EventWithEventContainer;
    onColumnValueChange: (columnId: string, value: any, eventTemplateId: Uuid | undefined) => void;
    /** Column config ids that failed required validation (edit mode). */
    invalidRequiredColumnIds?: ReadonlySet<string>;
    /** Template ids of other events on the same journey; powers overview counts when configured. */
    linkedJourneyEventTemplateIds?: Array<Uuid>;
};

/**
 * Component to render journey details columns based on the new journeyDetailsPage.columnGrouping configuration
 * Supports both editable (inline edit) and non-editable (view only) columns
 * For Event columns, returns eventTemplateId in the callback
 */
export function EventDetailsColumns({
    columnGrouping,
    journeysModuleConfiguration,
    eventTemplates,
    eventWithDiff,
    event,
    onColumnValueChange,
    linkedJourneyEventTemplateIds,
}: EventDetailsColumnsProps) {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const users = useUsers();
    const userTemplate = useUserTemplates();
    const btlActivities = useBtlActivities() as Array<BtlActivity>;
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const products = useProductCatalogue() ?? [];
    const {platformUserDetails, workspaceCapabilities, userDefinedObjectOptionsByTemplateId} = useWorkspace();
    const platformUserDetailsByUserId = useMemo(
        () => new Map(platformUserDetails.map((platformUserDetail) => [platformUserDetail.userId, platformUserDetail])),
        [platformUserDetails],
    );
    const productCatalogueTemplates = useProductCatalogueTemplates();
    const getEmotoradProductColorDisplayName = (product: ProductCatalogue): string => {
        const EMOTORAD__PRODUCT_CATALOGUE__COLOR_NAME__COLUMN_ID = "ec16634a-156b-48eb-9d32-1364f0b80f42" as Uuid;
        const colorName = product.additionalColumnValues[EMOTORAD__PRODUCT_CATALOGUE__COLOR_NAME__COLUMN_ID];
        return typeof colorName === "string" && colorName.trim() !== "" ? colorName : getProductCatalogueDisplayName(product, productCatalogueTemplates);
    };
    // Helper function to render columns
    const renderColumns = (columns: Array<EventColumnConfig>) => (
        <ItemBuilder
            items={columns}
            itemBuilder={(columnConfig) => {
                // Find the column definition based on columnType
                let column: UdoColumn | undefined;
                let eventTemplate: OttopilotEventTemplate | undefined;

                if (columnConfig.columnType === "OttopilotEventContainer") {
                    // Find column from journeysModuleConfiguration
                    column = getAdditionalColumns(journeysModuleConfiguration).find((col) => col.id === columnConfig.columnId);
                } else {
                    // Find column from the appropriate event template
                    const resolvedEventTemplateId = columnConfig.eventTemplateId ?? eventWithDiff.templateId;
                    if (resolvedEventTemplateId !== undefined) {
                        eventTemplate = eventTemplates.find((et) => et.id === resolvedEventTemplateId);
                        if (eventTemplate !== undefined) {
                            column = getAdditionalColumns(eventTemplate).find((col) => col.id === columnConfig.columnId);
                        }
                    }
                }

                const normalizedLabel = columnConfig.label.trim().toLowerCase();
                const isSyntheticDispositionLabel =
                    normalizedLabel === "l1 disposition" || normalizedLabel === "l2 disposition" || normalizedLabel === "l3 disposition";
                const isSyntheticWalkinLabel =
                    normalizedLabel === "walk-in count" ||
                    normalizedLabel === "with i cyle?" ||
                    normalizedLabel === "with i cycle?" ||
                    normalizedLabel === "last walk-in date" ||
                    normalizedLabel === "last v.l.h date";
                // Missing template metadata: still render using raw values (workspace UUIDs can differ from bundled constants).
                if (columnConfig.isAdditionalColumn && column === undefined && isSyntheticDispositionLabel !== true && isSyntheticWalkinLabel !== true) {
                    console.warn(
                        `Column metadata not found (read-only/fallback display): ${columnConfig.columnId} (columnType: ${columnConfig.columnType}, eventTemplateId: ${columnConfig.eventTemplateId})`,
                    );
                }

                // Get the value based on column type
                const getValue = () => {
                    if (columnConfig.columnType === "OttopilotEventContainer") {
                        // Journey columns - access via eventWithDiff.eventContainer
                        if (columnConfig.isAdditionalColumn) {
                            const journeyColumnId = columnConfig.columnId as Uuid;
                            if (eventWithDiff.templateId === (EmotoradEventTypes.testride as Uuid)) {
                                return resolveEmotoradTestrideJourneyGeoValue(eventWithDiff, journeyColumnId);
                            }
                            return eventWithDiff.eventContainer.additionalColumnValues[journeyColumnId];
                        } else {
                            // System columns like createdAt, updatedBy, etc.
                            return (eventWithDiff.eventContainer as unknown as Record<string, unknown>)[columnConfig.columnId];
                        }
                    } else {
                        // Event columns - access directly from eventWithDiff
                        if (columnConfig.eventTemplateId === undefined || eventWithDiff.templateId === columnConfig.eventTemplateId) {
                            if (columnConfig.isAdditionalColumn) {
                                return coalesceEmotoradWalkinTestRideTakenValue(eventWithDiff, columnConfig, normalizedLabel);
                            } else {
                                // System columns like createdAt, updatedBy, etc.
                                return (eventWithDiff as unknown as Record<string, unknown>)[String(columnConfig.columnId)];
                            }
                        }
                        return null;
                    }
                };

                // Get the original value for comparison
                const getOriginalValue = () => {
                    if (columnConfig.columnType === "OttopilotEventContainer") {
                        // Journey columns - access via event.eventContainer
                        if (columnConfig.isAdditionalColumn) {
                            const journeyColumnId = columnConfig.columnId as Uuid;
                            if (event.templateId === (EmotoradEventTypes.testride as Uuid)) {
                                return resolveEmotoradTestrideJourneyGeoValue(event, journeyColumnId);
                            }
                            return event.eventContainer.additionalColumnValues[journeyColumnId];
                        } else {
                            return (event.eventContainer as unknown as Record<string, unknown>)[columnConfig.columnId];
                        }
                    } else {
                        // Event columns - access directly from event
                        if (columnConfig.eventTemplateId === undefined || event.templateId === columnConfig.eventTemplateId) {
                            if (columnConfig.isAdditionalColumn) {
                                return coalesceEmotoradWalkinTestRideTakenValue(event, columnConfig, normalizedLabel);
                            } else {
                                return (event as unknown as Record<string, unknown>)[String(columnConfig.columnId)];
                            }
                        }
                        return null;
                    }
                };

                const value = getValue();
                const originalValue = getOriginalValue();
                const dispositionLevels = getDispositionLevelsFromEvent(eventWithDiff, eventTemplates);
                const walkinCountFromJourney =
                    eventWithDiff.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__WALKINS_COUNT__COLUMN_ID] ??
                    eventWithDiff.eventContainer.additionalColumnValues[EMOTORAD__CUSTOMERS__WALKINS_COUNT_COLUMN_ID];
                const lastWalkinDateFromEvent = eventWithDiff.additionalColumnValues[EMOTORAD__EVENT__WALKINS__LAST_WALKIN_DATE__COLUMN_ID];
                const syntheticDispositionValue = (() => {
                    if (normalizedLabel === "l1 disposition") return dispositionLevels.l1;
                    if (normalizedLabel === "l2 disposition") return dispositionLevels.l2;
                    if (normalizedLabel === "l3 disposition") return dispositionLevels.l3;
                    return null;
                })();
                const syntheticWalkinValue = (() => {
                    if (normalizedLabel === "walk-in count" || normalizedLabel === "with i cyle?" || normalizedLabel === "with i cycle?") {
                        return walkinCountFromJourney === null || walkinCountFromJourney === undefined ? "" : String(walkinCountFromJourney);
                    }
                    if (normalizedLabel === "last walk-in date" || normalizedLabel === "last v.l.h date") {
                        return lastWalkinDateFromEvent === null || lastWalkinDateFromEvent === undefined ? "" : String(lastWalkinDateFromEvent);
                    }
                    return null;
                })();

                // Get string representation for view-only columns
                const getStringValue = (): string => {
                    if (syntheticDispositionValue !== null && syntheticDispositionValue !== "") {
                        return syntheticDispositionValue;
                    }
                    if (syntheticWalkinValue !== null && syntheticWalkinValue !== "") {
                        return syntheticWalkinValue;
                    }
                    const emotoradCreatedByDisplay = resolveEmotoradCreatedByColumnDisplayIfApplicable(getAdditionalColumns, columnConfig, {
                        row: eventWithDiff,
                        journeysModuleConfiguration,
                        eventTemplates,
                        users,
                        userTemplates: userTemplate,
                        platformUserDetailsByUserId,
                        organizationalUnits,
                        organizationalUnitTemplates,
                        btlActivities,
                        products,
                        userDefinedObjectOptionsByTemplateId,
                    });
                    if (emotoradCreatedByDisplay !== null) {
                        return emotoradCreatedByDisplay;
                    }
                    if (value === null || value === undefined) {
                        return "-";
                    }

                    const emotoradAssignedDealerDisplay = tryFormatEmotoradAssignedDealerJourneyValue(
                        columnConfig,
                        value,
                        organizationalUnits,
                        organizationalUnitTemplates,
                    );
                    if (emotoradAssignedDealerDisplay !== undefined && emotoradAssignedDealerDisplay.trim() !== "") {
                        return emotoradAssignedDealerDisplay;
                    }

                    // For additional columns (isAdditionalColumn === true)
                    if (columnConfig.isAdditionalColumn && column !== undefined) {
                        if (columnConfig.columnType === "OttopilotEventContainer") {
                            if (column.type === UdoColumnType.user) {
                                return getUserDisplayName(value as Uuid, users, userTemplate, platformUserDetailsByUserId);
                            }
                            // Use journey stringValueGetter
                            const stringValue = getUserDefinedObjectAdditionalColumnDetails(column).stringValueGetter({
                                value: value as any,
                                users: users,
                                userTemplates: userTemplate,
                                organizationalUnits: organizationalUnits,
                                organizationalUnitTemplates: organizationalUnitTemplates,
                                products: products,
                            });
                            if (
                                stringValue === "?" &&
                                (column.id === EMOTORAD__EVENT__ENQUIRY__GENDER__COLUMN_ID || column.id === EMOTORAD__USER__GENDER__COLUMN_ID)
                            ) {
                                const match = Object.values(emotoradGenders).find((s) => s.id === value);
                                if (match) return match.displayName;
                            }
                            return stringValue;
                        } else {
                            if (column.type === UdoColumnType.user) {
                                return getUserDisplayName(value as Uuid, users, userTemplate, platformUserDetailsByUserId);
                            }
                            if (DULUX_EVENT_TEMPLATE_EVENT_OWNER_COLUMN_IDS.some((id) => id === column.id)) {
                                const display = getUserDisplayName(value as Uuid, users, userTemplate, platformUserDetailsByUserId);
                                return display !== "?" ? display : String(value ?? "-");
                            }
                            if (
                                columnConfig.columnType === "OttopilotEvent" &&
                                eventTemplate !== undefined &&
                                eventTemplate.id === (EmotoradEventTypes.testride as Uuid) &&
                                String(columnConfig.columnId) === String(EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID)
                            ) {
                                return formatEmotoradTestRideDateOnlyForDisplay(value);
                            }
                            if (
                                columnConfig.columnType === "OttopilotEvent" &&
                                eventTemplate !== undefined &&
                                eventTemplate.id === (EmotoradEventTypes.testride as Uuid) &&
                                String(columnConfig.columnId) === String(EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_TIME__COLUMN_ID)
                            ) {
                                const slot = getEmotoradTestRideEffectiveSlotHhMmFromEventRow({
                                    additionalColumnValues: eventWithDiff.additionalColumnValues,
                                    eventContainer: {additionalColumnValues: eventWithDiff.eventContainer.additionalColumnValues},
                                });
                                if (slot === "") {
                                    return "-";
                                }
                                return formatEmotoradTestRideShortRangeLabelFromSlotHhMm(slot);
                            }
                            // Use event stringValueGetter
                            const stringValue = getUserDefinedObjectAdditionalColumnDetails(column).stringValueGetter({
                                value: value as any,
                                users: users,
                                userTemplates: userTemplate,
                                organizationalUnits: organizationalUnits,
                                organizationalUnitTemplates: organizationalUnitTemplates,
                                products: products,
                            });
                            if (
                                stringValue === "?" &&
                                (column.id === EMOTORAD__EVENT__ENQUIRY__GENDER__COLUMN_ID || column.id === EMOTORAD__USER__GENDER__COLUMN_ID)
                            ) {
                                const match = Object.values(emotoradGenders).find((s) => s.id === value);
                                if (match) return match.displayName;
                            }
                            return stringValue;
                        }
                    }

                    // For system columns (isAdditionalColumn === false)
                    // Resolve system-column formatting by column name.
                    const columnId = columnConfig.columnId as string;
                    const isDuluxEventSystemColumn =
                        isDuluxWorkspace(workspaceCapabilities) === true &&
                        columnConfig.columnType === "OttopilotEvent" &&
                        isDuluxEventTemplateId(eventWithDiff.templateId) === true;
                    if (columnId === "createdBy" || columnId === "updatedBy") {
                        if (isDuluxEventSystemColumn === true) {
                            return formatDuluxEventUserReferenceDisplay(value, {
                                users: users,
                                userTemplates: userTemplate,
                                platformUserDetailsByUserId: platformUserDetailsByUserId,
                            });
                        }
                        return getUserDisplayName(value as Uuid, users, userTemplate, platformUserDetailsByUserId);
                    }
                    if ((columnId === "createdAt" || columnId === "updatedAt") && isDuluxEventSystemColumn === true) {
                        return formatOttopilotDisplayDateTime(String(value ?? ""));
                    }
                    const systemColumnDetails = getSystemAdditionalColumnDetails(columnId);
                    if (systemColumnDetails !== undefined) {
                        return systemColumnDetails.stringValueGetter({
                            value,
                            users: users,
                            userTemplates: userTemplate,
                            platformUserDetailsByUserId,
                        });
                    }

                    // Fallback: just return string representation
                    return formatEmotoradScalarForUnknownColumnDisplay(value);
                };

                // Get options for select columns
                const getOptions = () => {
                    if (column !== undefined && "configuration" in column && column.configuration && "options" in column.configuration) {
                        return (
                            column.configuration as {
                                options: Array<{id: Uuid; displayName: string}>;
                            }
                        ).options;
                    }
                    return undefined;
                };

                const options = getOptions();

                const preferredColorDisplay = (() => {
                    const normalizedLabel = columnConfig.label.trim().toLowerCase();
                    if (normalizedLabel !== "preferred colour" && normalizedLabel !== "preferred color") {
                        return null;
                    }
                    const matchedOption = options?.find((item) => item.id === value);
                    let displayText = matchedOption?.displayName ?? getStringValue();

                    // Specific logic for Emotorad Preferred Color to avoid showing price
                    if (displayText !== "-" && (normalizedLabel === "preferred colour" || normalizedLabel === "preferred color")) {
                        const product = products.find((p) => p.id === value);
                        if (product) {
                            displayText = getEmotoradProductColorDisplayName(product);
                        }
                    }
                    const rawValue = value === null || value === undefined ? "" : String(value).trim();
                    const hexCandidateFromRaw = rawValue.match(/#(?:[0-9a-fA-F]{3}){1,2}\b/)?.[0];
                    const hexCandidateFromText = displayText.match(/#(?:[0-9a-fA-F]{3}){1,2}\b/)?.[0];
                    const swatchColor = hexCandidateFromRaw ?? hexCandidateFromText;
                    if (displayText === "-" && swatchColor === undefined) {
                        return null;
                    }
                    return (
                        <div className="p-ds-space-100 text-[0.875rem] leading-ds-space-250 break-words flex items-center gap-ds-space-075">
                            {swatchColor !== undefined ? (
                                <span
                                    aria-hidden="true"
                                    className="inline-block h-ds-space-150 w-ds-space-150 rounded-full border border-ds-border"
                                    style={{backgroundColor: swatchColor}}
                                />
                            ) : null}
                            <span style={{color: token("color.text")}}>{displayText}</span>
                        </div>
                    );
                })();

                // Render inline edit component based on column type
                const renderInlineEditComponent = () => {
                    const handleSetValue = (nextState: any) => {
                        if (nextState === originalValue) {
                            return;
                        }
                        // Pass eventTemplateId if this is an Event column, undefined otherwise
                        onColumnValueChange(
                            columnConfig.columnId as string,
                            nextState,
                            columnConfig.columnType === "OttopilotEvent" ? columnConfig.eventTemplateId : undefined,
                        );
                    };

                    if (
                        columnConfig.columnType === "OttopilotEvent" &&
                        String(columnConfig.columnId) === String(AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID)
                    ) {
                        return (
                            <div className="min-w-0">
                                <DateTimePicker
                                    placeholder="Pick date and time"
                                    dropdownType="modal"
                                    size="md"
                                    minDate={new Date()}
                                    timePickerProps={{
                                        withDropdown: true,
                                        popoverProps: {withinPortal: false},
                                        format: "24h",
                                    }}
                                    value={iso8601DateTimeUtcToTriggerSchedulePickerValue(value)}
                                    onChange={(nextValue) => {
                                        if (nextValue === null) {
                                            handleSetValue(null);
                                            return;
                                        }
                                        const asDate = typeof nextValue === "string" ? new Date(nextValue) : nextValue;
                                        if (Number.isNaN(asDate.getTime()) === true) {
                                            return;
                                        }
                                        if (asDate.getTime() < Date.now()) {
                                            return;
                                        }
                                        handleSetValue(triggerSchedulePickerValueToIso8601DateTimeUtc(nextValue));
                                    }}
                                />
                            </div>
                        );
                    }

                    if (column === undefined) {
                        return null;
                    }

                    const productCatalogueTemplateId = getProductCatalogueTemplateIdFromAdditionalColumnConfiguration("configuration" in column ? column.configuration : undefined);

                    const defaultValue = {
                        label: (() => {
                            if (options !== undefined) {
                                const matchedOption = options.find((item) => item.id === value);
                                return matchedOption?.displayName ?? "-";
                            }

                            // Specific logic for Emotorad Product labels in edit panel
                            const isProductType =
                                column.type === UdoColumnType.product;
                            if (isProductType) {
                                const product = products.find((p) => p.id === value);
                                if (product) {
                                    return getEmotoradProductColorDisplayName(product);
                                }
                            }

                            return "Select an option";
                        })(),
                        value: value,
                    };

                    if (columnConfig.columnType === "OttopilotEventContainer") {
                        // Use journey inlineEditComponent
                        return getUserDefinedObjectAdditionalColumnDetails(column).inlineEditComponent({
                            value: value as any,
                            setValue: handleSetValue,
                            options: options as any,
                            defaultValue: defaultValue,
                            productCatalogueTemplateId: productCatalogueTemplateId,
                            organizationalUnitId: getEventContainerOrganizationalUnitId(eventWithDiff.eventContainer, journeysModuleConfiguration),
                        });
                    } else {
                        // Use event inlineEditComponent
                        return getUserDefinedObjectAdditionalColumnDetails(column).inlineEditComponent({
                            value: value as any,
                            setValue: handleSetValue,
                            options: options as any,
                            defaultValue: defaultValue,
                            productCatalogueTemplateId: productCatalogueTemplateId,
                            organizationalUnitId: getEventContainerOrganizationalUnitId(eventWithDiff.eventContainer, journeysModuleConfiguration),
                        });
                    }
                };

                return (
                    <div
                        className="grid grid-cols-[1fr_1fr] justify-start gap-4 px-ds-space-150 py-ds-space-100"
                        key={columnConfig.id}
                    >
                        <div
                            className="font-ds-font-weight-medium leading-ds-space-250 text-[0.875rem] p-ds-space-100 break-words"
                            style={{
                                color: token("color.text"),
                                font: "Inter",
                            }}
                            id={columnConfig.id}
                        >
                            <div className="w-full break-words">{columnConfig.label}</div>
                        </div>
                        <div className="break-words min-w-0">
                            {columnConfig.isEditable === false || column === undefined || column.source.type === UdoColumnSource.resolver
                                ? // Non-editable: show as view-only text
                                  (preferredColorDisplay ?? (
                                      <div
                                          className="p-ds-space-100 text-[0.875rem] leading-ds-space-250 break-words"
                                          style={{
                                              color: token("color.text"),
                                          }}
                                      >
                                          {getStringValue()}
                                      </div>
                                  ))
                                : // Editable: use inlineEditComponent
                                  renderInlineEditComponent()}
                        </div>
                    </div>
                );
            }}
            spaceBuilder={(spaceIndex) => (
                <AnankeTempVerticalSpacer
                    above="formRow"
                    below="formRow"
                    key={spaceIndex}
                />
            )}
        />
    );

    const overviewTemplateIds = linkedJourneyEventTemplateIds ?? [];

    // If only one column group, render columns directly without collapsible section
    if (columnGrouping.length === 1) {
        const onlyGroup = columnGrouping[0]!;
        if (isEventOverviewSection(onlyGroup)) {
            return (
                <LinkedJourneyEventsOverviewSection
                    title={onlyGroup.displayName ?? "Events on this lead"}
                    eventTemplates={eventTemplates}
                    linkedJourneyEventTemplateIds={overviewTemplateIds}
                    initialIsOpen={onlyGroup.initialIsOpen}
                    className={onlyGroup.className}
                />
            );
        }
        return renderColumns(onlyGroup.columns);
    }

    // Multiple column groups: wrap each in a collapsible section
    return (
        <ItemBuilder
            items={columnGrouping}
            itemBuilder={(columnGroup) => {
                if (isEventOverviewSection(columnGroup)) {
                    return (
                        <LinkedJourneyEventsOverviewSection
                            key={columnGroup.id}
                            title={columnGroup.displayName ?? "Events on this lead"}
                            eventTemplates={eventTemplates}
                            linkedJourneyEventTemplateIds={overviewTemplateIds}
                            initialIsOpen={columnGroup.initialIsOpen}
                            className={columnGroup.className}
                        />
                    );
                }
                return (
                    <AnankeTempFormCollapsibleSection
                        title={columnGroup.displayName}
                        key={columnGroup.id}
                    >
                        {renderColumns(columnGroup.columns)}
                    </AnankeTempFormCollapsibleSection>
                );
            }}
            spaceBuilder={(spaceIndex) => (
                <AnankeTempVerticalSpacer
                    above="collapsibleSectionEnd"
                    below="collapsibleSectionStart"
                    key={spaceIndex}
                />
            )}
        />
    );
}

export function EventDetailsEditColumns({
    columnGrouping,
    journeysModuleConfiguration,
    eventTemplates,
    eventWithDiff,
    onColumnValueChange,
    invalidRequiredColumnIds,
}: EventDetailsColumnsProps) {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const users = useUsers();
    const userTemplate = useUserTemplates();
    const btlActivities = useBtlActivities() as Array<BtlActivity>;
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const products = useProductCatalogue() ?? [];
    const {platformUserDetails, workspaceCapabilities, userDefinedObjectOptionsByTemplateId} = useWorkspace();
    const platformUserDetailsByUserId = useMemo(
        () => new Map(platformUserDetails.map((platformUserDetail) => [platformUserDetail.userId, platformUserDetail])),
        [platformUserDetails],
    );
    const columnOnlyGrouping = columnGrouping.filter(isColumnGroupSection);

    // Helper function to render columns
    // Helper function to render columns
    const renderColumns = (columns: Array<EventColumnConfig>) => {
        const oddColumns = columns.filter((_, index) => (index + 1) % 2 === 1);
        const evenColumns = columns.filter((_, index) => (index + 1) % 2 === 0);
        const rowCount = Math.max(oddColumns.length, evenColumns.length);

        const renderColumnField = (columnConfig: EventColumnConfig | undefined): {columnId: Uuid; label: ReactElement; field: ReactElement} | null => {
            if (!columnConfig) return null;

            // Find the column definition based on columnType
            let column: UdoColumn | undefined;
            let eventTemplate: OttopilotEventTemplate | undefined;

            if (columnConfig.columnType === "OttopilotEventContainer") {
                // Find column from journeysModuleConfiguration
                column = getAdditionalColumns(journeysModuleConfiguration).find((col) => col.id === columnConfig.columnId);
            } else {
                // Find column from the appropriate event template
                const resolvedEventTemplateId = columnConfig.eventTemplateId ?? eventWithDiff.templateId;
                if (resolvedEventTemplateId !== undefined) {
                    eventTemplate = eventTemplates.find((et) => et.id === resolvedEventTemplateId);
                    if (eventTemplate !== undefined) {
                        column = getAdditionalColumns(eventTemplate).find((col) => col.id === columnConfig.columnId);
                    }
                }
            }

            const normalizedLabel = columnConfig.label.trim().toLowerCase();
            const isSyntheticDispositionLabel =
                normalizedLabel === "l1 disposition" || normalizedLabel === "l2 disposition" || normalizedLabel === "l3 disposition";
            const isSyntheticWalkinLabel =
                normalizedLabel === "walk-in count" ||
                normalizedLabel === "with i cyle?" ||
                normalizedLabel === "with i cycle?" ||
                normalizedLabel === "last walk-in date" ||
                normalizedLabel === "last v.l.h date";
            // Missing template metadata: still render using raw values (workspace UUIDs can differ from bundled constants).
            if (columnConfig.isAdditionalColumn && column === undefined && isSyntheticDispositionLabel !== true && isSyntheticWalkinLabel !== true) {
                console.warn(
                    `Column metadata not found (read-only/fallback display): ${columnConfig.columnId} (columnType: ${columnConfig.columnType}, eventTemplateId: ${columnConfig.eventTemplateId})`,
                );
            }

            // Get the value based on column type
            const getValue = () => {
                if (columnConfig.columnType === "OttopilotEventContainer") {
                    // Journey columns - access via eventWithDiff.eventContainer
                    if (columnConfig.isAdditionalColumn) {
                        const journeyColumnId = columnConfig.columnId as Uuid;
                        if (eventWithDiff.templateId === (EmotoradEventTypes.testride as Uuid)) {
                            return resolveEmotoradTestrideJourneyGeoValue(eventWithDiff, journeyColumnId);
                        }
                        return eventWithDiff.eventContainer.additionalColumnValues[journeyColumnId];
                    } else {
                        // System columns like createdAt, updatedBy, etc.
                        return (eventWithDiff.eventContainer as unknown as Record<string, unknown>)[columnConfig.columnId];
                    }
                } else {
                    // Event columns - access directly from eventWithDiff
                    if (columnConfig.eventTemplateId === undefined || eventWithDiff.templateId === columnConfig.eventTemplateId) {
                        if (columnConfig.isAdditionalColumn) {
                            return coalesceEmotoradWalkinTestRideTakenValue(eventWithDiff, columnConfig, normalizedLabel);
                        } else {
                            // System columns like createdAt, updatedBy, etc.
                            return (eventWithDiff as unknown as Record<string, unknown>)[String(columnConfig.columnId)];
                        }
                    }
                    return null;
                }
            };

            const value = getValue();
            const dispositionLevels = getDispositionLevelsFromEvent(eventWithDiff, eventTemplates);
            const walkinCountFromJourney =
                eventWithDiff.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__WALKINS_COUNT__COLUMN_ID] ??
                eventWithDiff.eventContainer.additionalColumnValues[EMOTORAD__CUSTOMERS__WALKINS_COUNT_COLUMN_ID];
            const lastWalkinDateFromEvent = eventWithDiff.additionalColumnValues[EMOTORAD__EVENT__WALKINS__LAST_WALKIN_DATE__COLUMN_ID];
            const syntheticDispositionValue = (() => {
                if (normalizedLabel === "l1 disposition") return dispositionLevels.l1;
                if (normalizedLabel === "l2 disposition") return dispositionLevels.l2;
                if (normalizedLabel === "l3 disposition") return dispositionLevels.l3;
                return null;
            })();
            const syntheticWalkinValue = (() => {
                if (normalizedLabel === "walk-in count" || normalizedLabel === "with i cyle?" || normalizedLabel === "with i cycle?") {
                    return walkinCountFromJourney === null || walkinCountFromJourney === undefined ? "" : String(walkinCountFromJourney);
                }
                if (normalizedLabel === "last walk-in date" || normalizedLabel === "last v.l.h date") {
                    return lastWalkinDateFromEvent === null || lastWalkinDateFromEvent === undefined ? "" : String(lastWalkinDateFromEvent);
                }
                return null;
            })();

            // Get string representation for view-only columns
            const getStringValue = (): string => {
                if (syntheticDispositionValue !== null && syntheticDispositionValue !== "") {
                    return syntheticDispositionValue;
                }
                if (syntheticWalkinValue !== null && syntheticWalkinValue !== "") {
                    return syntheticWalkinValue;
                }
                const emotoradCreatedByDisplay = resolveEmotoradCreatedByColumnDisplayIfApplicable(getAdditionalColumns, columnConfig, {
                    row: eventWithDiff,
                    journeysModuleConfiguration,
                    eventTemplates,
                    users,
                    userTemplates: userTemplate,
                    platformUserDetailsByUserId,
                    organizationalUnits,
                    organizationalUnitTemplates,
                    btlActivities,
                    products,
                    userDefinedObjectOptionsByTemplateId,
                });
                if (emotoradCreatedByDisplay !== null) {
                    return emotoradCreatedByDisplay;
                }
                if (
                    columnConfig.columnType === "OttopilotEventContainer" &&
                    columnConfig.isAdditionalColumn === true &&
                    (String(columnConfig.columnId) === String(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_CONTACT__COLUMN_ID) ||
                        String(columnConfig.columnId) === String(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_EMAIL__COLUMN_ID))
                ) {
                    return resolveColumnValue(
                        getAdditionalColumns,
                        {
                            id: columnConfig.id,
                            label: columnConfig.label,
                            columnId: columnConfig.columnId,
                            isAdditionalColumn: true,
                            columnType: "OttopilotEventContainer",
                        },
                        {
                            row: eventWithDiff,
                            journeysModuleConfiguration,
                            eventTemplates,
                            users,
                            userTemplates: userTemplate,
                            platformUserDetailsByUserId,
                            organizationalUnits,
                            organizationalUnitTemplates,
                            btlActivities,
                            products,
                            userDefinedObjectOptionsByTemplateId,
                        },
                    );
                }
                if (value === null || value === undefined) {
                    return "-";
                }

                const emotoradAssignedDealerDisplay = tryFormatEmotoradAssignedDealerJourneyValue(
                    columnConfig,
                    value,
                    organizationalUnits,
                    organizationalUnitTemplates,
                );
                if (emotoradAssignedDealerDisplay !== undefined && emotoradAssignedDealerDisplay.trim() !== "") {
                    return emotoradAssignedDealerDisplay;
                }

                // For additional columns (isAdditionalColumn === true)
                if (columnConfig.isAdditionalColumn && column !== undefined) {
                    if (columnConfig.columnType === "OttopilotEventContainer") {
                        if (column.type === UdoColumnType.user) {
                            return getUserDisplayName(value as Uuid, users, userTemplate, platformUserDetailsByUserId);
                        }
                        // Use journey stringValueGetter
                        return getUserDefinedObjectAdditionalColumnDetails(column).stringValueGetter({
                            value: value as any,
                            users: users,
                            userTemplates: userTemplate,
                            organizationalUnits: organizationalUnits,
                            organizationalUnitTemplates: organizationalUnitTemplates,
                            products: products,
                        });
                    } else {
                        if (column.type === UdoColumnType.user) {
                            return getUserDisplayName(value as Uuid, users, userTemplate, platformUserDetailsByUserId);
                        }
                        if (DULUX_EVENT_TEMPLATE_EVENT_OWNER_COLUMN_IDS.some((id) => id === column.id)) {
                            const display = getUserDisplayName(value as Uuid, users, userTemplate, platformUserDetailsByUserId);
                            return display !== "?" ? display : String(value ?? "-");
                        }
                        if (
                            columnConfig.columnType === "OttopilotEvent" &&
                            eventTemplate !== undefined &&
                            eventTemplate.id === (EmotoradEventTypes.testride as Uuid) &&
                            String(columnConfig.columnId) === String(EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID)
                        ) {
                            return formatEmotoradTestRideDateOnlyForDisplay(value);
                        }
                        if (
                            columnConfig.columnType === "OttopilotEvent" &&
                            eventTemplate !== undefined &&
                            eventTemplate.id === (EmotoradEventTypes.testride as Uuid) &&
                            String(columnConfig.columnId) === String(EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_TIME__COLUMN_ID)
                        ) {
                            const slot = getEmotoradTestRideEffectiveSlotHhMmFromEventRow({
                                additionalColumnValues: eventWithDiff.additionalColumnValues,
                                eventContainer: {additionalColumnValues: eventWithDiff.eventContainer.additionalColumnValues},
                            });
                            if (slot === "") {
                                return "-";
                            }
                            return formatEmotoradTestRideShortRangeLabelFromSlotHhMm(slot);
                        }
                        // Use event stringValueGetter
                        return getUserDefinedObjectAdditionalColumnDetails(column).stringValueGetter({
                            value: value as any,
                            users: users,
                            userTemplates: userTemplate,
                            organizationalUnits: organizationalUnits,
                            organizationalUnitTemplates: organizationalUnitTemplates,
                            products: products,
                        });
                    }
                }

                // For system columns (isAdditionalColumn === false)
                // Resolve system-column formatting by column name.
                const columnId = columnConfig.columnId as string;
                const isDuluxEventSystemColumn =
                    isDuluxWorkspace(workspaceCapabilities) === true &&
                    columnConfig.columnType === "OttopilotEvent" &&
                    isDuluxEventTemplateId(eventWithDiff.templateId) === true;
                if (columnId === "createdBy" || columnId === "updatedBy") {
                    if (isDuluxEventSystemColumn === true) {
                        return formatDuluxEventUserReferenceDisplay(value, {
                            users: users,
                            userTemplates: userTemplate,
                            platformUserDetailsByUserId: platformUserDetailsByUserId,
                        });
                    }
                    return getUserDisplayName(value as Uuid, users, userTemplate, platformUserDetailsByUserId);
                }
                if ((columnId === "createdAt" || columnId === "updatedAt") && isDuluxEventSystemColumn === true) {
                    return formatOttopilotDisplayDateTime(String(value ?? ""));
                }
                const systemColumnDetails = getSystemAdditionalColumnDetails(columnId);
                if (systemColumnDetails !== undefined) {
                    return systemColumnDetails.stringValueGetter({
                        value,
                        users: users,
                        userTemplates: userTemplate,
                        platformUserDetailsByUserId,
                    });
                }

                // Fallback: just return string representation
                return formatEmotoradScalarForUnknownColumnDisplay(value);
            };

            // Get options for select columns
            const getOptions = () => {
                if (column !== undefined && "configuration" in column && column.configuration && "options" in column.configuration) {
                    const raw = (
                        column.configuration as {
                            options: Array<{id: Uuid; displayName: string}>;
                        }
                    ).options;
                    if (columnConfig.columnType === "OttopilotEvent" && String(columnConfig.columnId) === String(EMOTORAD__EVENT__SALES__MODEL_VARIANT__COLUMN_ID)) {
                        return filterEmotoradSalesModelVariantSelectOptions({
                            fullOptions: raw,
                            products,
                            selectedModelId: eventWithDiff.additionalColumnValues[EMOTORAD__EVENT__SALES__MODEL__COLUMN_ID],
                            currentVariantId: value,
                        });
                    }
                    if (columnConfig.columnType === "OttopilotEvent" && String(columnConfig.columnId) === String(EMOTORAD__EVENT__SALES__PREFERRED_COLOR__COLUMN_ID)) {
                        return filterEmotoradSalesPreferredColorSelectOptions({
                            fullOptions: raw,
                            products,
                            selectedModelId: eventWithDiff.additionalColumnValues[EMOTORAD__EVENT__SALES__MODEL__COLUMN_ID],
                            selectedVariantId: eventWithDiff.additionalColumnValues[EMOTORAD__EVENT__SALES__MODEL_VARIANT__COLUMN_ID],
                            currentColorId: value,
                        });
                    }
                    return raw;
                }
                return undefined;
            };

            const options = getOptions();

            // Render inline edit component based on column type
            const renderInlineEditComponent = () => {
                const handleSetValue = (nextState: any) => {
                    if (nextState === value) {
                        return;
                    }
                    onColumnValueChange(
                        columnConfig.columnId as string,
                        nextState,
                        columnConfig.columnType === "OttopilotEvent" ? columnConfig.eventTemplateId : undefined,
                    );
                };

                if (columnConfig.columnType === "OttopilotEvent" && String(columnConfig.columnId) === String(DULUX__EVENT__CALLS__SCHEDULE_DATE__COLUMN_ID)) {
                    const schedulePickerValue = (() => {
                        if (value === null || value === undefined) {
                            return null;
                        }
                        if (value instanceof Date) {
                            return value;
                        }
                        if (typeof value === "string") {
                            const parsed = new Date(value);
                            if (Number.isNaN(parsed.getTime()) === false) {
                                return parsed;
                            }
                        }
                        return null;
                    })();
                    return (
                        <div className="min-w-0">
                            <DateTimePicker
                                placeholder="Pick date and time"
                                dropdownType="modal"
                                size="md"
                                minDate={new Date()}
                                timePickerProps={{
                                    withDropdown: true,
                                    popoverProps: {withinPortal: false},
                                    format: "12h",
                                }}
                                value={schedulePickerValue}
                                onChange={(nextValue) => {
                                    handleSetValue(nextValue);
                                }}
                            />
                        </div>
                    );
                }

                if (
                    columnConfig.columnType === "OttopilotEvent" &&
                    String(columnConfig.columnId) === String(AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID)
                ) {
                    return (
                        <div className="min-w-0">
                            <DateTimePicker
                                placeholder="Pick date and time"
                                dropdownType="modal"
                                size="md"
                                minDate={new Date()}
                                timePickerProps={{
                                    withDropdown: true,
                                    popoverProps: {withinPortal: false},
                                    format: "24h",
                                }}
                                value={iso8601DateTimeUtcToTriggerSchedulePickerValue(value)}
                                onChange={(nextValue) => {
                                    if (nextValue === null) {
                                        handleSetValue(null);
                                        return;
                                    }
                                    const asDate = typeof nextValue === "string" ? new Date(nextValue) : nextValue;
                                    if (Number.isNaN(asDate.getTime()) === true) {
                                        return;
                                    }
                                    if (asDate.getTime() < Date.now()) {
                                        return;
                                    }
                                    handleSetValue(triggerSchedulePickerValueToIso8601DateTimeUtc(nextValue));
                                }}
                            />
                        </div>
                    );
                }

                if (columnConfig.columnType === "OttopilotEvent" && String(columnConfig.columnId) === String(EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID)) {
                    const {pickerDate} = splitEmotoradTestRideStoredInstantToPickerDateAndSlot(value);
                    const slotHhMm =
                        getEmotoradTestRideEffectiveSlotHhMmFromEventRow({
                            additionalColumnValues: eventWithDiff.additionalColumnValues,
                            eventContainer: {additionalColumnValues: eventWithDiff.eventContainer.additionalColumnValues},
                        }) || splitEmotoradTestRideStoredInstantToPickerDateAndSlot(value).slotHhMm;
                    const fallbackSlot = "08:00";
                    const effectiveSlot = slotHhMm !== "" ? slotHhMm : fallbackSlot;
                    return (
                        <div className="min-w-0">
                            <DatePickerInput
                                value={pickerDate}
                                onChange={(d) => {
                                    const next = parseEmotoradTestRideDatePickerDayToLocalDate(d);
                                    if (next === null) {
                                        handleSetValue(null);
                                        return;
                                    }
                                    const iso = combineEmotoradTestRidePickerDateAndSlotHhMmToUtcIso(next, effectiveSlot);
                                    handleSetValue(iso ?? null);
                                }}
                                valueFormat="DD/MM/YYYY"
                                placeholder="Pick date"
                                dropdownType="popover"
                                popoverProps={{
                                    withinPortal: false,
                                    closeOnClickOutside: false,
                                    trapFocus: false,
                                    zIndex: 10001,
                                }}
                                size="md"
                                clearable={true}
                            />
                        </div>
                    );
                }

                if (columnConfig.columnType === "OttopilotEvent" && String(columnConfig.columnId) === String(EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_TIME__COLUMN_ID)) {
                    const hourlySlotOptions = getEmotoradTestRideHourlyTimeSlotOptions();
                    const dateRaw =
                        eventWithDiff.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID] ??
                        eventWithDiff.eventContainer.additionalColumnValues[EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID];
                    const {pickerDate} = splitEmotoradTestRideStoredInstantToPickerDateAndSlot(dateRaw);
                    const effectiveSlotFromRow = getEmotoradTestRideEffectiveSlotHhMmFromEventRow({
                        additionalColumnValues: eventWithDiff.additionalColumnValues,
                        eventContainer: {additionalColumnValues: eventWithDiff.eventContainer.additionalColumnValues},
                    });
                    const timeStr = value === null || value === undefined || value === "" ? "" : String(value).trim();
                    const slotFromTimeColumn = (() => {
                        if (timeStr === "") {
                            return "";
                        }
                        const fromLabelOrRange = normalizeEmotoradTestRideSlotHhMmString(timeStr);
                        if (fromLabelOrRange !== "") {
                            return fromLabelOrRange;
                        }
                        const m = /^(\d{1,2}):(\d{2})$/.exec(timeStr);
                        if (m === null) {
                            return "";
                        }
                        const h = Number(m[1]);
                        const min = Number(m[2]);
                        if (Number.isNaN(h) === true || Number.isNaN(min) === true) {
                            return "";
                        }
                        return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
                    })();
                    const slotFromValue = effectiveSlotFromRow !== "" ? effectiveSlotFromRow : slotFromTimeColumn;
                    const fallbackSlot = "08:00";
                    const effectiveSlot = slotFromValue !== "" ? slotFromValue : fallbackSlot;
                    const slotOptions =
                        slotFromValue !== "" && hourlySlotOptions.some((o) => o.value === slotFromValue) !== true
                            ? [
                                  {
                                      value: slotFromValue,
                                      label:
                                          formatEmotoradTestRideTimeSlotDisplay(slotFromValue) ||
                                          formatEmotoradTestRideShortRangeLabelFromSlotHhMm(slotFromValue),
                                  },
                                  ...hourlySlotOptions,
                              ]
                            : hourlySlotOptions;
                    const selectedSlot = slotOptions.find((o) => o.value === effectiveSlot) ?? null;
                    return (
                        <div className="min-w-0">
                            <AnankeSelect
                                isDisabled={pickerDate === null}
                                options={slotOptions.map((o) => ({value: o.value, label: o.label}))}
                                value={selectedSlot === null || pickerDate === null ? null : {value: selectedSlot.value, label: selectedSlot.label}}
                                onChange={(nv: unknown) => {
                                    const nextSlot =
                                        typeof nv === "object" &&
                                        nv !== null &&
                                        "value" in nv &&
                                        nv.value !== undefined &&
                                        nv.value !== null
                                            ? String(nv.value)
                                            : "";
                                    handleSetValue(nextSlot !== "" ? nextSlot : null);
                                }}
                            />
                        </div>
                    );
                }

                if (column === undefined) {
                    return null;
                }

                const productCatalogueTemplateId = getProductCatalogueTemplateIdFromAdditionalColumnConfiguration("configuration" in column ? column.configuration : undefined);

                const allowedProductIdsForPicker =
                    column !== undefined && column.type === UdoColumnType.product && columnConfig.columnType === "OttopilotEvent"
                        ? String(columnConfig.columnId) === String(EMOTORAD__EVENT__SALES__MODEL_VARIANT__COLUMN_ID)
                            ? getEmotoradSalesModelVariantAllowedProductIds(
                                  products,
                                  eventWithDiff.additionalColumnValues[EMOTORAD__EVENT__SALES__MODEL__COLUMN_ID],
                                  value,
                              )
                            : String(columnConfig.columnId) === String(EMOTORAD__EVENT__SALES__PREFERRED_COLOR__COLUMN_ID)
                              ? getEmotoradSalesPreferredColorAllowedProductIds(
                                    products,
                                    eventWithDiff.additionalColumnValues[EMOTORAD__EVENT__SALES__MODEL__COLUMN_ID],
                                    eventWithDiff.additionalColumnValues[EMOTORAD__EVENT__SALES__MODEL_VARIANT__COLUMN_ID],
                                    value,
                                )
                              : undefined
                        : undefined;

                if (columnConfig.columnType === "OttopilotEventContainer") {
                    // Use journey editComponent
                    return getUserDefinedObjectAdditionalColumnDetails(column).editComponent({
                        value: value as any,
                        setValue: handleSetValue,
                        options: options as any,
                        productCatalogueTemplateId: productCatalogueTemplateId,
                        allowedProductIds: allowedProductIdsForPicker,
                        organizationalUnitId: getEventContainerOrganizationalUnitId(eventWithDiff.eventContainer, journeysModuleConfiguration),
                    });
                } else {
                    // Use event editComponent
                    return getUserDefinedObjectAdditionalColumnDetails(column).editComponent({
                        value: value as any,
                        setValue: handleSetValue,
                        options: options as any,
                        productCatalogueTemplateId: productCatalogueTemplateId,
                        allowedProductIds: allowedProductIdsForPicker,
                        organizationalUnitId: getEventContainerOrganizationalUnitId(eventWithDiff.eventContainer, journeysModuleConfiguration),
                    });
                }
            };

            const label = (
                <div
                    className="font-ds-font-weight-medium leading-ds-space-250 text-[0.875rem] break-words"
                    style={{color: token("color.text"), font: "Inter"}}
                    id={columnConfig.id}
                >
                    <div className="w-full break-words">
                        {columnConfig.label}
                        {columnConfig.isRequired === true ? (
                            <span
                                style={{color: token("color.text.danger", "#AE2E24")}}
                                aria-hidden={true}
                            >
                                {" "}
                                *
                            </span>
                        ) : null}
                    </div>
                </div>
            );

            const field = (
                <div className="break-words min-w-0">
                    {columnConfig.isEditable === false || column === undefined || column.source.type === UdoColumnSource.resolver ? (
                        options !== undefined ? (
                            <AnankeSelect
                                options={options.map((option) => ({label: option.displayName, value: option.id}))}
                                value={
                                    options.map((option) => ({label: option.displayName, value: option.id})).find((option) => option.value === value) ?? null
                                }
                                isDisabled={true}
                            />
                        ) : (
                            <AnankeTextField
                                value={getStringValue()}
                                isDisabled={true}
                                readOnly={true}
                                isInvalid={invalidRequiredColumnIds?.has(String(columnConfig.id)) === true}
                            />
                        )
                    ) : (
                        <div
                            className="min-w-0"
                            style={
                                invalidRequiredColumnIds?.has(String(columnConfig.id)) === true
                                    ? {
                                          borderRadius: "var(--ds-border-radius-100, 4px)",
                                          boxShadow: `0 0 0 2px ${token("color.border.danger", "#E34935")}`,
                                      }
                                    : undefined
                            }
                        >
                            {renderInlineEditComponent()}
                        </div>
                    )}
                    {invalidRequiredColumnIds?.has(String(columnConfig.id)) === true ? (
                        <div
                            className="mt-ds-space-050 text-[0.75rem]"
                            style={{color: token("color.text.danger", "#AE2E24")}}
                        >
                            This field is required
                        </div>
                    ) : null}
                </div>
            );

            return {columnId: columnConfig.id, label, field};
        };

        if (columns.length <= 1) {
            return (
                <div className="grid grid-flow-row gap-y-ds-space-200 py-ds-space-100 px-ds-space-150">
                    {columns.map((col) => {
                        const parts = renderColumnField(col);
                        if (parts === null) {
                            return null;
                        }
                        return (
                            <div
                                key={parts.columnId}
                                className="grid grid-rows-[max-content_max-content] gap-y-ds-space-050"
                            >
                                {parts.label}
                                {parts.field}
                            </div>
                        );
                    })}
                </div>
            );
        }

        return (
            <div className="grid grid-flow-row gap-y-ds-space-200 py-ds-space-100 px-ds-space-150">
                {Array.from({length: rowCount}).map((_, rowIndex) => {
                    const isLastRow = rowIndex === rowCount - 1;
                    const oddBuilt = renderColumnField(oddColumns[rowIndex]);
                    const evenBuilt = renderColumnField(evenColumns[rowIndex]);
                    return (
                        <div key={`row-${rowIndex}`}>
                            <div className="grid grid-cols-2 grid-rows-[max-content_max-content] gap-x-ds-space-300 gap-y-ds-space-050">
                                <div className="min-w-0">{oddBuilt?.label ?? null}</div>
                                <div className="min-w-0">{evenBuilt?.label ?? null}</div>
                                <div className="min-w-0">{oddBuilt?.field ?? null}</div>
                                <div className="min-w-0">{evenBuilt?.field ?? null}</div>
                            </div>
                            {!isLastRow && (
                                <AnankeTempVerticalSpacer
                                    above="formRow"
                                    below="formRow"
                                    key={`spacer-${rowIndex}`}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // If only one column group, render columns directly without collapsible section
    if (columnOnlyGrouping.length === 1) {
        return renderColumns(columnOnlyGrouping[0]!.columns);
    }

    // Multiple column groups: wrap each in a collapsible section
    return (
        <ItemBuilder
            items={columnOnlyGrouping}
            itemBuilder={(columnGroup) => (
                <AnankeTempFormCollapsibleSection
                    title={columnGroup.displayName}
                    key={columnGroup.id}
                >
                    {renderColumns(columnGroup.columns)}
                </AnankeTempFormCollapsibleSection>
            )}
            spaceBuilder={(spaceIndex) => (
                <AnankeTempVerticalSpacer
                    above="collapsibleSectionEnd"
                    below="collapsibleSectionStart"
                    key={spaceIndex}
                />
            )}
        />
    );
}
